<template>
  <Slideout
    :id="`zipcode-slideout-${id}`"
    title="Where are you shipping to?"
    @close-slideout="handleSlideoutClose"
  >
    <div aria-live="assertive">
      <Notification
        v-if="zipcodeEmptyStore && zipCodeEmptyErrorVisibility"
        severity="error"
        :closable="false"
      >
        Please enter a valid zip code
      </Notification>
      <Notification
        v-if="zipCodeLengthError"
        severity="error"
        :closable="false"
      >
        Please enter 5 digit valid zip code
      </Notification>
      <Notification
        v-else-if="isZipCodeError"
        severity="error"
        :closable="false"
      >
        Your items cannot be shipped to this zip code. Please try an alternate zip code.
      </Notification>
    </div>
    <div class="grid-x">
      <div class="cell small-12">
        <div class="input-container">
          <div class="input-inner-start">
            <div class="search-large" />
          </div>
          <input
            ref="inputSearch"
            v-model="inputZipCode"
            class="input-search"
            placeholder="Search by zip"
            type="text"
            maxlength="5"
            :disabled="isZipCodeValidationLoading"
            @input="zipCodeVerify"
            @keyup.enter="getGeoLocation"
          >
          <div
            class="input-inner-end search-input-cta-container"
          >
            <button
              v-if="inputZipCode?.length"
              class="clear-filled search-input-cta"
              aria-label="clear search"
              @click="clearHandler"
            />
          </div>
        </div>
      </div>
    </div>
    <template #footer>
      <div class="grid-x grid-margin-x">
        <button
          type="submit"
          class="primary button expanded zipcode-apply-btn"
          :disabled="isZipCodeValidationLoading"
          @click="getGeoLocation"
        >
          Apply
        </button>
      </div>
    </template>
  </Slideout>
</template>

<script>
import {
  computed, ref, watch, onMounted, nextTick,
} from 'vue';
import { Cookie } from '@vue-util/cookie';
import { Slideout } from '@atomic-ui/slideout';
import { fireTag } from '@vue-util/tagManager';
import { useStore } from 'vuex';
import { locations } from '@common-vue/locator/src/store';
import { FETCH_GEOCODE } from '@common-vue/locator/src/types/actions';
import { SET_FULFILLMENT_ZIPCODE_EMPTY_STORE } from '@common-vue/locator/src/types/mutations';
import Notification from '@atomic-ui/notification';

export default {
  name: 'ZipcodeSlideout',
  components: {
    Slideout, Notification,
  },
  props: {
    id: {
      type: String,
      default: '',
    },
    zipCode: {
      type: String,
      default: '',
    },
    page: {
      type: String,
      default: '',
    },
    updateShipZipCodeCallback: {
      type: Function,
    },
    productAvailabilityInfo: {
      type: Object,
      required: false,
      default: () => ({}),
    },
  },
  emits: ['update:zipCode', 'closed:zipCode'],
  setup(props, { emit }) {
    const store = useStore();
    const locationType = computed(() => (process.env.ENTRY_POINT.toUpperCase() === 'MCOM' ? 'MSTORE' : 'BSTORE'));
    if (!store.hasModule('locations')) {
      store.registerModule('locations', locations);
    }
    const inputZipCode = ref(props.zipCode);
    const inputSearch = ref(null);
    const isZipCodeError = ref(false);
    const isZipCodeValidationLoading = ref(false);
    const zipCodeEmptyErrorVisibility = ref(false);
    const zipCodeLengthError = ref(false);
    const zipcodeEmptyStore = computed(() => store.state.locations.fulfillmentZipcodeEmptyStore);

    const handleSlideoutClose = () => {
      emit('closed:zipCode', true);
      store.state.locations.fulfillmentZipcodeEmptyStore = false;
      inputZipCode.value = props.zipCode;
      zipCodeLengthError.value = false;
    };

    const fireInitialZipcodeTags = () => {
      // TODO: Placeholder tags, these will be updated when we get updated tags from analytics teams
      if (props.page === 'pdp' || props.page === 'bag') {
        const zipcodeTagsObject = {
          action_location: props.page === 'bag' ? `${props.page}-body`.toUpperCase() : `${props.page}-body`,
          action_misc: inputZipCode.value,
          postalCode: inputZipCode.value,
          site_action: 'ship zipcode change start',
        };
        if (props.page === 'bag') {
          zipcodeTagsObject.product_id = [props.id];
        }
        fireTag('link', zipcodeTagsObject);
      }
      if (props.page === 'plp' || props.page === 'browse') {
        fireTag('link', {
          site_action: 'ship zipcode change start',
          action_location: 'left nav',
          action_misc: inputZipCode.value,
          postalCode: inputZipCode.value,
        });
      }
    };

    const fireUpdatedZipcodeTags = () => {
      const { sddAvailability, orderType, available } = props.productAvailabilityInfo || {};
      if (props.page === 'pdp' || props.page === 'bag') {
        const updatedZipcodeTagsObject = {
          action_location: props.page === 'bag' ? `${props.page}-body`.toUpperCase() : `${props.page}-body`,
          action_misc: inputZipCode.value,
          postalCode: inputZipCode.value,
          site_action: 'ship zipcode change complete',
          event_name: props.page === 'pdp' ? 'product sdd flag' : 'bag ship zipcode change complete',
          product_id: [props.id],
          sdd_eligibility_flag: [sddAvailability && available ? 'true' : 'false'],

        };
        if (props.page === 'pdp') {
          const upperCaseOrderType = orderType ? orderType.toUpperCase() : '';
          let deliveryType;

          if (upperCaseOrderType === 'EXT') {
            deliveryType = 'marketplace';
          } else if (upperCaseOrderType === 'DROP') {
            deliveryType = 'vendor';
          } else {
            deliveryType = 'own';
          }
          updatedZipcodeTagsObject.delivery_type = deliveryType;
        }
        fireTag('link', updatedZipcodeTagsObject);
      }
      if (props.page === 'plp' || props.page === 'browse') {
        fireTag('link', {
          action_location: 'left nav',
          action_misc: inputZipCode.value,
          postalCode: inputZipCode.value,
          site_action: 'ship zipcode change complete',
        });
      }
    };

    onMounted(() => {
      fireInitialZipcodeTags();
    });

    const handleUpdateZipCode = (emitZipcode) => {
      fireUpdatedZipcodeTags();
      if (props.page !== 'registry') {
        Cookie.set('BTZIPCODE', inputZipCode.value, 'MISCGCs');
      }
      store.commit('slideoutStore/TOGGLE', `zipcode-slideout-${props.id}`);
      nextTick(() => {
        emit('update:zipCode', inputZipCode.value, emitZipcode);
        emit('closed:zipCode', true);
      });
    };

    const onUpdateShipZipCode = async () => {
      // Zipcode callback handler
      if (props.updateShipZipCodeCallback) {
        const callback = await props.updateShipZipCodeCallback(inputZipCode.value);
        if (callback?.success) {
          handleUpdateZipCode();
          return;
        }
        isZipCodeError.value = true;
        return;
      }
      handleUpdateZipCode(true);
    };

    const testZipcode = (zipcode) => /\b\d{5}\b/.test(zipcode); // Test for exactly 5 digits, will not match the first 5 of a 6+ digit input

    const getGeoLocation = async () => {
      isZipCodeValidationLoading.value = true;

      if (!inputZipCode.value) {
        store.commit(`locations/${SET_FULFILLMENT_ZIPCODE_EMPTY_STORE}`, true);
      } else if (!testZipcode(inputZipCode.value)) {
        zipCodeLengthError.value = true;
        isZipCodeValidationLoading.value = false;
        return;
      } else {
      // Calling locator geocode api call
        await store.dispatch(`locations/${FETCH_GEOCODE}`, {
          address: inputZipCode.value,
          locationType: locationType.value,
          client: 'fulfillment',
        });
        if (!zipcodeEmptyStore.value && inputZipCode.value) {
          await onUpdateShipZipCode();
          isZipCodeValidationLoading.value = false;
          return;
        }
      }
      zipCodeEmptyErrorVisibility.value = true;
      isZipCodeValidationLoading.value = false;
    };

    const zipCodeVerify = (e) => {
      inputZipCode.value = e.target.value.replace(/\D/g, '');
      zipCodeEmptyErrorVisibility.value = false;
      isZipCodeError.value = false;
      zipCodeLengthError.value = false;
    };

    const clearHandler = () => {
      inputZipCode.value = '';
      zipCodeEmptyErrorVisibility.value = false;
      isZipCodeError.value = false;
      zipCodeLengthError.value = false;
      inputSearch.value.focus();
    };

    watch(() => props.zipCode, (data) => {
      inputZipCode.value = data;
    });

    return {
      inputZipCode,
      fireUpdatedZipcodeTags,
      fireInitialZipcodeTags,
      zipcodeEmptyStore,
      inputSearch,
      locationType,
      handleUpdateZipCode,
      handleSlideoutClose,
      getGeoLocation,
      clearHandler,
      zipCodeVerify,
      isZipCodeError,
      zipCodeEmptyErrorVisibility,
      onUpdateShipZipCode,
      isZipCodeValidationLoading,
      zipCodeLengthError,
    };
  },
};
</script>

<style lang="scss" scoped>
@import "~@core/csg/src/scss/forms/forms.scss";
@include csg-forms;
@import "@csgIcons/_error.scss";
@import "@csgIcons/_error-red.scss";
@import "@csgIcons/_search-large.scss";
@import "@csgIcons/_clear-filled.scss";
.search-input-cta-container{
  display: flex;
  align-items: center;
}
.search-input-cta{
  cursor: pointer;
}
</style>
